import type { TFunction } from 'i18next'
import type { CharacterSheet } from '@rollcall/contracts'
import { ROUTES } from './routes.js'
import { contentName } from './ui/labels.js'

/**
 * Подпись персонажа в шапке: вид, класс, подкласс и уровень.
 *
 * Раньше в шапке стояло «Лист персонажа» — верно для любого экрана
 * и бесполезно на каждом. Кто именно перед тобой, на листе не было
 * написано нигде: имя пряталось в заголовке вкладки «Персонаж»,
 * а вид с классом — только в разборе величин.
 *
 * Собирается из листа, а не из выборов создания: подкласс приходит
 * шагом повышения (D31), и «выбранное при создании» его не знает.
 * Порядок — как в источнике на листе персонажа (с. 20): вид, класс,
 * подкласс, уровень.
 */
export function characterSubtitle(t: TFunction, sheet: CharacterSheet): string {
  const named = (type: 'species' | 'class' | 'subclass'): string | null => {
    const found = Object.entries(sheet.sources).find(
      ([, source]) => source.type === type,
    )
    return found === undefined ? null : contentName(t, found[0], found[1].name)
  }

  const parts = [named('species'), named('class'), named('subclass')].filter(
    (part): part is string => part !== null,
  )
  parts.push(t('sheet.levelShort', { level: sheet.level }))
  return parts.join(' · ')
}

/**
 * Заголовок шапки, когда персонажа нет.
 *
 * «Лист персонажа» стояло на всех экранах разом — и на входе,
 * и в списке персонажей, и в кампании. Верно для любого экрана
 * и бесполезно на каждом: шапка обязана называть то, что под ней.
 *
 * Порядок проверок — от частного к общему: адрес кампании начинается
 * с адреса списка кампаний, и общий пришлось бы ставить последним
 * в любом случае. Незнакомый адрес получает имя приложения: там
 * стоит экран «такого адреса нет», и называть его в шапке значило бы
 * сообщить об ошибке дважды.
 */
export function screenTitle(t: TFunction, path: string): string {
  const at = (route: string): boolean => path === route
  const under = (route: string): boolean => path.startsWith(`${route}/`)

  if (at(ROUTES.start)) return t('start.title')
  if (at(ROUTES.home)) return t('home.title')
  if (at(ROUTES.newCharacter)) return t('wizard.title')
  if (at(ROUTES.characters) || under(ROUTES.characters)) return t('characters.title')
  if (at(ROUTES.about)) return t('about.title')
  if (at(ROUTES.whatsNew)) return t('whatsNew.title')
  // Новая запись и правка лежат под /content: заголовок у них общий —
  // «Свои записи», а чем именно занят экран, говорит шаг под шапкой.
  if (at(ROUTES.content) || under(ROUTES.content)) return t('custom.title')
  if (at(ROUTES.campaigns) || under(ROUTES.campaigns)) return t('campaigns.title')
  if (at(ROUTES.signIn)) return t('auth.signIn')
  if (at(ROUTES.signUp)) return t('auth.signUp')
  if (at(ROUTES.forgotPassword)) return t('auth.forgotTitle')
  if (at(ROUTES.confirmEmail)) return t('auth.confirmTitle')
  if (at(ROUTES.resetPassword)) return t('auth.resetTitle')
  if (at(ROUTES.join)) return t('join.title')

  return t('app.name')
}
